import { Device, type DeviceRepository } from '@telespot/domain';

import { ParseBaseRepository } from '../../parse-base.repository';
import { OrganizationTopology } from '../organization';
import { DeviceTopology, ParseDeviceMapper } from './parse-device.mapper';
import { DeviceTypeTopology } from './parse-device-type.mapper';
import { ObjectTopology } from '../../parse.topology';

export class ParseDeviceRepository extends ParseBaseRepository implements DeviceRepository {
  private static readonly COUNT_DEVICES_PAGINATION_LIMIT = 25;

  private readonly deviceMapper = new ParseDeviceMapper(this.parse);

  public async listByOrganization(
    organizationId: string,
    searchTerm?: string,
    limit?: number,
    lastItemCreatedAt?: Date
  ): Promise<Device[]> {
    const query = this.buildDeviceQuery(organizationId, searchTerm);

    query.include(DeviceTopology.TYPE);
    query.descending(ObjectTopology.CREATED_AT);

    if (limit) {
      query.limit(limit);
    }

    if (lastItemCreatedAt) {
      query.lessThan(ObjectTopology.CREATED_AT, lastItemCreatedAt);
    }

    const devices = await query.find(this.options);
    return devices.map((parseDevice) => this.deviceMapper.toDomain(parseDevice));
  }

  public async count(organizationId?: string, searchTerm?: string): Promise<number> {
    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = this.buildDeviceQuery(organizationId, searchTerm);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseDeviceRepository.COUNT_DEVICES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseDeviceRepository.COUNT_DEVICES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  private buildDeviceQuery(organizationId?: string, searchTerm?: string): Parse.Query {
    const query = new this.parse.Query(DeviceTopology.TABLE);
    if (organizationId) {
      query.equalTo(
        DeviceTopology.ORGANIZATION,
        this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId)
      );
    }

    if (searchTerm && searchTerm.trim() !== '') {
      query.matches(DeviceTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    return query;
  }

  public async anyForDeviceType(deviceTypeId: string): Promise<boolean> {
    const parseDeviceType = this.subclasses.getSubclass(DeviceTypeTopology.TABLE).createWithoutData(deviceTypeId);

    const result = await new this.parse.Query(DeviceTopology.TABLE)
      .equalTo(DeviceTopology.TYPE, parseDeviceType)
      .select()
      .limit(1)
      .first(this.options);

    return result !== undefined;
  }

  public async countByDeviceType(deviceTypeId: string): Promise<number> {
    const parseDeviceType = this.subclasses.getSubclass(DeviceTypeTopology.TABLE).createWithoutData(deviceTypeId);

    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = new this.parse.Query(DeviceTopology.TABLE);
      query.equalTo(DeviceTopology.TYPE, parseDeviceType);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseDeviceRepository.COUNT_DEVICES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseDeviceRepository.COUNT_DEVICES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }
}
